import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { changePassword, logout, ApiRequestError, type MeResponse, getSpend, type SpendResponse } from "@app/lib/api";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";

interface AccountProps {
  me: MeResponse;
}

const SPEND_CACHE_TTL_SECONDS = 300; // 5 minutes

export default function Account({ me }: AccountProps) {
  const nav = useNavigate();
  const onSignOut = async () => {
    try {
      await logout();
    } finally {
      nav("/login", { replace: true });
    }
  };

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okAt, setOkAt] = useState<number | null>(null);

  const [spend, setSpend] = useState<SpendResponse | null>(null);
  const [spendLoading, setSpendLoading] = useState(true);
  const [spendErr, setSpendErr] = useState<string | null>(null);

  const fetchSpend = async (force = false) => {
    try {
      setSpendLoading(true);
      setSpendErr(null);
      const data = await getSpend();
      setSpend(data);
      // If cache is stale and we're about to display it, refetch
      if (!force && data.source === "cache") {
        const ageSeconds = (Date.now() - data.ts) / 1000;
        if (ageSeconds > SPEND_CACHE_TTL_SECONDS) {
          await fetchSpend(true);
        }
      }
    } catch (e) {
      if (e instanceof ApiRequestError) {
        setSpendErr(e.code === "no_key" ? "No OpenRouter key configured" : e.message);
      } else {
        setSpendErr(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setSpendLoading(false);
    }
  };

  useEffect(() => {
    fetchSpend();
  }, []);

  const onChangePw = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setOkAt(null);
    if (next.length < 12) {
      setErr("New password must be at least 12 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setOkAt(Date.now());
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      if (e instanceof ApiRequestError) {
        setErr(e.message);
      } else {
        setErr(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-2xl font-semibold">Account</h1>
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Signed in identity for this Hermes.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <div className="text-xs uppercase tracking-wide text-muted-fg">Email</div>
            <div className="text-sm">{me.email}</div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-xs uppercase tracking-wide text-muted-fg">User ID</div>
            <div className="font-mono text-xs text-muted-fg">{me.id}</div>
          </div>
          <div>
            <Button variant="outline" onClick={onSignOut}>
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>LLM spend</CardTitle>
          <CardDescription>Your API key usage and quota.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {spendErr ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
              {spendErr}
            </div>
          ) : spendLoading ? (
            <div className="text-sm text-muted-fg">Loading spend data...</div>
          ) : spend ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col gap-1">
                  <div className="text-xs uppercase tracking-wide text-muted-fg">Usage</div>
                  <div className="text-sm font-semibold">${(spend.usageCents / 100).toFixed(2)}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-xs uppercase tracking-wide text-muted-fg">Limit</div>
                  <div className="text-sm font-semibold">${(spend.limitCents / 100).toFixed(2)}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-xs uppercase tracking-wide text-muted-fg">Remaining</div>
                  <div className="text-sm font-semibold">${(spend.remainingCents / 100).toFixed(2)}</div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="text-xs text-muted-fg">
                  {spend.source === "cache"
                    ? `Cached ${Math.round((Date.now() - spend.ts) / 1000)}s ago`
                    : "Live from OpenRouter"}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchSpend(true)}
                  disabled={spendLoading}
                >
                  {spendLoading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
          <CardDescription>
            Minimum 12 characters. All other active sessions for your account will be
            signed out.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onChangePw} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="current">Current password</Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="next">New password</Label>
              <Input
                id="next"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={12}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={12}
              />
            </div>
            {err ? (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                {err}
              </div>
            ) : null}
            {okAt ? (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
                Password updated at {new Date(okAt).toLocaleTimeString()}.
              </div>
            ) : null}
            <div>
              <Button type="submit" disabled={busy}>
                {busy ? "Updating..." : "Update password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
